import { cloneDeep } from 'lodash'

import events$ from '@shared/services/Events'

import { logInfo } from '@shared/helpers/Logger'
import { DateTimeHelper } from '@shared/helpers/DateTime'
import { haXProducts } from '@shared/utils/HaX'

import basketAPI from '@shared/api/basket'
import productAPI from '@shared/api/product'
import basketStore from '@shared/store/basket'
import productStructureStore from '@shared/store/productStructure'
import useContract from '@shared/hooks/useContract'
import useI18n from '@shared/hooks/useI18n'
import usePerson from '@shared/hooks/usePerson'
import usePersonDetails from '@shared/hooks/usePersonDetails'
import useProduct from '@shared/hooks/useProduct'

import { isRequired } from '@shared/utils/CustomErrors'
import {
  getSortedProductsFromGroup,
  getSortedGroupsFromCategory,
  getSortedProductsFromCategory,
  sortProducts,
  updatePriceOptions,
} from '@shared/utils/Product'
import { SortUtil } from '@shared/utils/Sorting'

import { CATEGORY_TYPE, NOTIFICATION, PRODUCT_MODIFICATION_TYPE, PRODUCT_STATE } from '@shared/config/constants'
import { EVENT_PRODUCT } from '@shared/config/events'
import { updatePersonInBasket } from '@shared/utils/Person'
import { removeUnusedDoctors } from '@shared/utils/Doctor'

export default function usePersonSpecificProduct() {
  /**
   * @file usePersonSpecificProduct contains logic for products involving a single specific person
   */

  // METHODS
  async function customizeProduct(payload, source) {
    const { categoryId, personId, productId, option } = payload

    let __basket = await selectProductOption({ personId, productId, option })
    __basket = await basketStore.updateOvpBasket(__basket)

    events$.emit(EVENT_PRODUCT.CHANGED, {
      basket: __basket,
      categoryId,
      option,
      personId,
      productId,
      source,
    })

    return __basket
  }

  function getCategoriesByPersonId(personId = isRequired('personId')) {
    const { getPerson } = usePerson()
    const personCategories = getPerson(personId)?.products?.categories ?? {}

    if (!personCategories) return []

    return getSortedCategories(productStructureStore.categories.value)
      .filter(category => !!personCategories[category.id])
      .map(category => Object.assign(category, personCategories[category.id]))
      .sort(SortUtil.sortByOrder)
  }

  function getCategory(categoryId = isRequired('categoryId'), personId = isRequired('personId')) {
    return getCategoriesByPersonId(personId).find(c => c.id === categoryId)
  }

  /**
   * getGroupsFromCategory
   * @param {string} personId
   * @param {string} categoryId
   * @returns {Category}
   */
  function getGroupsFromCategory(personId, categoryId) {
    const { getPerson } = usePerson()
    const __groups = getPerson(personId)?.products?.groups ?? {}

    return getProductGroupsFromCategory(categoryId)
      .filter(group => !!__groups[group.id])
      .map(group => Object.assign(group, __groups[group.id]))
      .sort(SortUtil.sortByOrder)
  }

  /**
   * getPriceCalculationsForProduct calculates all the price variants for person's product
   * @note: the result of this can't be used for e.g. the productModal, as it wont contain everything related to the contract/offer
   * @param {String} productId
   * @param {Object} person
   *
   * @returns {Promise<PriceCombination[]>}
   *
   */
  async function getPriceCalculationsForProduct(productId, person) {
    const { getCategoryFromProduct } = useProduct()
    const selectedProduct = getProduct(person.personId, productId)
    const selectedPrice = selectedProduct.prices.find(p => p.selected)
    const productCategory = getCategoryFromProduct(productId)
    const payload = {
      address: basketStore.basket.address,
      collective: basketStore.basket.collective,
      collectivePersonGroup: person.collectivePersonGroup,
      contractNumber: basketStore.basket.contractNumber,
      contractStartDate:
        productCategory.id === CATEGORY_TYPE.KVG ? person.kvgContractStartDate : person.vvgContractStartDate,
      dateOfBirth: person.personData.dateOfBirth,
      gender: person.personData.gender,
      masterTreaty: basketStore.basket.masterTreaty,
      selectedPriceCombination: selectedPrice,
      zsrNumber: selectedProduct.doctor?.zsrNumber,
    }

    return await productAPI.calculateAllPricesForProduct(productId, payload)
  }

  async function getSimulatedProductSelection({
    productId = null,
    product = {},
    personId,
    option = null,
    basket = cloneDeep(basketStore.basket),
  }) {
    const { isUpgrade } = useProduct()
    let isSelected = product.selected

    const mutateProducts = () => {
      const mode = isUpgrade(productId) ? 'ADD' : 'REPLACE'

      const pBasket = mutateProductsInBasketForPerson(personId, productId, { basket, mode })
      return updatePersonInBasket(basket, personId, pBasket)
    }

    if (Object.keys(product).length) {
      product.selected = true
      productId = product.productId

      basket = mutateProducts()
    } else {
      isSelected = basket.persons.find(p => p.personId === personId).products.products[productId].selected

      basket = mutateProducts()
      product = basket.persons.find(p => p.personId === personId).products.products[productId]
    }

    if (option) {
      basket = await selectProductOption({
        personId,
        product: Object.assign({}, { productId }, product),
        option,
        basket,
      })
    }

    basket = await basketAPI.simulateUpdateBasket({ personId, selectedCalculations: true }, basket)

    return Object.assign({}, product, basket.persons.find(p => p.personId === personId).products.products[productId], {
      productId,
      selected: isSelected,
    })
  }

  /**
   * getProduct returns the product depending on the personId and productId
   * @param {string} personId
   * @param {string} productId
   * @returns {Product}
   */
  function getProduct(personId, productId) {
    const { getPerson } = usePerson()
    return getPerson(personId)?.products?.products[productId]
  }

  function getProductMessages({ categoryId, hasCalculationWarnings, personId, productState, product }) {
    const { isUnborn, getPerson } = usePerson()
    const { t, te } = useI18n()
    const data = []

    const _person = getPerson(personId)
    const _age = DateTimeHelper.getAge(_person.personData.dateOfBirth, _person.kvgContractStartDate)

    if (productState !== PRODUCT_STATE.EDITABLE) {
      data.push({
        closeable: false,
        content: t('product.detail.cancellation.message'),
        severity: NOTIFICATION.WARNING,
      })
    }

    if (hasCalculationWarnings) {
      data.push({
        closeable: false,
        content: t('product.detail.message', { servicePortalText: product.calculationWarnings[0] }),
        severity: NOTIFICATION.INFO,
      })
    }

    const key = `products.${product.productId}`
    if (isUnborn(personId) && te(`${key}.additionalInfoUnborn`)) {
      data.push({
        closeable: false,
        content: t(`${key}.additionalInfoUnborn`),
        severity: NOTIFICATION.INFO,
      })
    } else if (te(`${key}.additionalInfo`, 'products')) {
      data.push({
        closeable: false,
        content: t(`${key}.additionalInfo`),
        severity: NOTIFICATION.INFO,
      })
    }

    // @note: FLEX-3284
    if (product.productId === 'Z04Produkt' && _age >= 5) {
      data.push({
        content: t('products.Z04Produkt.ageMessage.text'),
        severity: NOTIFICATION.INFO,
      })
    }

    // @note: FLEX-4043
    if (['SSHB01Produkt', 'SSPB01Produkt'].includes(product.productId) && _age < 1) {
      data.push({
        content: t(`products.${product.productId}.ageCaveat`),
        severity: NOTIFICATION.INFO,
      })
    }

    if (
      !hasCalculationWarnings &&
      product.riskAssessmentRequired === false &&
      product.selected === false &&
      categoryId !== CATEGORY_TYPE.KVG
    ) {
      data.push({
        closeable: false,
        content: t('screen.products.noRiskAssessmentRequired'),
        severity: NOTIFICATION.INFO,
      })
    }

    if (!product.prices && te(`products.${product.productId}.unavailable`, 'products')) {
      data.push({
        closeable: false,
        content: t(`products.${product.productId}.unavailable`),
        severity: NOTIFICATION.WARNING,
      })
    }

    const errors = product?.messages?.filter(m => m.type !== 'info') || []
    if (errors.length > 0) {
      errors.forEach(error => {
        data.push({
          actions: [],
          closeable: false,
          content: t(`error.${error.key}`),
          severity: error.type.toLowerCase(),
        })
      })
    }

    return data
  }

  /**
   * getProductPrice returns the price of a product for a given personId and productId
   * @param {string} personId
   * @param {string} productId
   * @returns {Product}
   */
  function getProductPrice(personId, productId) {
    return getProduct(personId, productId).prices.find(p => !!p.selected)
  }

  /**
   * getProductsFromCategory
   * @param {string} categoryId
   * @param {string} personId
   * @returns {}
   */
  function getProductsFromCategory(categoryId, personId) {
    const __products = personId ? getPersonProducts(personId) : productStructureStore.products.value
    return sortProducts(
      getSortedProductsFromCategory({
        categories: productStructureStore.categories.value,
        groups: productStructureStore.groups.value,
        products: __products,
        categoryId,
      }),
      __products
    )
  }

  /**
   * getProductsFromGroup
   * @param {string} groupId
   * @param {string} personId
   * @returns {}
   */
  function getProductsFromGroup(groupId, personId) {
    const __products = personId ? getPersonProducts(personId) : productStructureStore.products.value

    // pass groups and products from structure, not from the person -> because we need the config-flags from the group/product, e.g. medicalOffice
    return sortProducts(
      getSortedProductsFromGroup(productStructureStore.groups.value, productStructureStore.products.value, groupId),
      __products
    )
  }

  /**
   * Groups the selected products of a user by the category
   * and sorts both the products and the categories by order
   * @param {Array} selectedProducts Selected products of a person
   * @return []
   */
  function getSelectedProductsByCategory(selectedProducts = isRequired('selected products')) {
    if (selectedProducts.length === 0) return selectedProducts

    const { getCategoryIdFromProduct, getGroupFromProduct } = useProduct()
    const __selectedProducts = cloneDeep(selectedProducts)

    const productsByCategory = {}
    const unknownProducts = []

    Object.values(__selectedProducts).forEach(__product => {
      const categoryId = getCategoryIdFromProduct(__product.productId)
      const group = getGroupFromProduct(__product.productId)

      // add the grouping to the order
      __product.sort = `${group?.order}-${__product.order}`

      categoryId
        ? (productsByCategory[categoryId] = [...(productsByCategory[categoryId] || []), __product])
        : unknownProducts.push(__product)
    })

    unknownProducts.forEach(__product => {
      const categoryId = Object.keys(productsByCategory).find(c =>
        productsByCategory[c].find(pc => pc?.upgrades?.includes(__product.productId))
      )
      if (categoryId) {
        // Upgrade products
        productsByCategory[categoryId].push(__product)
      } else {
        // Old products without currently existing category (used for 'Bestandeskunden')
        productsByCategory[CATEGORY_TYPE.UNKONWN] = [...(productsByCategory[CATEGORY_TYPE.UNKONWN] || []), __product]
      }
    })

    Object.keys(productsByCategory).forEach(categoryId => {
      productsByCategory[categoryId].sort(SortUtil.sortByParam('sort'))
    })

    return (
      Object.keys(productsByCategory)
        .map(categoryId => {
          return { id: categoryId, products: productsByCategory[categoryId] }
        })
        /**
         * Get order of category with given categoryId
         * If category not found, return high order number to show this category as last
         * (needed for existing customers with old products)
         */
        .sort(
          SortUtil.sortByParam(
            item => Object.values(productStructureStore.categories.value).find(c => c.id === item.id)?.order || 1000
          )
        )
    )
  }

  /**
   * getSortedCategories returns all categories in an array sorted by order field
   * @param categories
   * @return []
   */
  function getSortedCategories(categories = isRequired('categories')) {
    const data = []
    const categoriesCopy = cloneDeep(categories)
    Object.keys(categoriesCopy).forEach(categoryId => data.push(categoriesCopy[categoryId]))
    return data.sort(SortUtil.sortByOrder)
  }

  /**
   *  hasPersonKVG returns true if a person has at least one KVG product selected
   *  @param {object} __selectedProducts
   *  @returns {boolean}
   */
  function hasPersonKVG(__selectedProducts) {
    const productCategories = getSelectedProductsByCategory(__selectedProducts)

    return productCategories.some(category => category.id === CATEGORY_TYPE.KVG)
  }

  /**
   *  hasPersonKVGModified returns true if a person has at least one KVG product selected marked as modified (added or changed)
   *  @param {object} __selectedProducts
   *  @returns {boolean}
   */
  function hasPersonKVGModified(__selectedProducts) {
    const filteredSelectedProducts = __selectedProducts.filter(
      product =>
        product.selected &&
        [PRODUCT_MODIFICATION_TYPE.NEW, PRODUCT_MODIFICATION_TYPE.CHANGED].includes(product.modificationType)
    )

    const productCategories = getSelectedProductsByCategory(filteredSelectedProducts)

    return productCategories.some(category => category.id === CATEGORY_TYPE.KVG)
  }

  /**
   *  hasPersonVVGModified returns true if a person has at least one VVG product selected marked as modified (added or changed)
   *  @param {object} __selectedProducts
   *  @returns {boolean}
   */
  function hasPersonVVGModified(__selectedProducts) {
    const filteredSelectedProducts = __selectedProducts.filter(
      product =>
        product.selected &&
        [PRODUCT_MODIFICATION_TYPE.NEW, PRODUCT_MODIFICATION_TYPE.CHANGED].includes(product.modificationType)
    )

    const productCategories = getSelectedProductsByCategory(filteredSelectedProducts)

    return productCategories.filter(category => category.id !== CATEGORY_TYPE.KVG).length > 0
  }

  /**
   *  hasPersonOnlyKVG returns true if a person has only selected a KVG product (but no VVG products)
   *  @todo circular dependency with usePerson
   *  @param {string} personId
   *  @returns {boolean}
   */
  function hasPersonOnlyKVG(personId) {
    const { getPersonWithDetails } = usePersonDetails()
    const __selectedProducts = getPersonWithDetails(personId).selectedProducts
    const productCategories = getSelectedProductsByCategory(__selectedProducts)
    return productCategories.length === 1 && productCategories[0].id === CATEGORY_TYPE.KVG
  }

  /**
   *  hasPersonOnlyVVG returns true if a person has only VVG products selected (but no KVG product)
   *  @todo circular dependency with usePerson
   *  @param {string} personId
   *  @returns {boolean}
   */
  function hasPersonOnlyVVG(personId) {
    const { getPersonWithDetails } = usePersonDetails()
    const __selectedProducts = getPersonWithDetails(personId).selectedProducts
    const productCategories = getSelectedProductsByCategory(__selectedProducts)
    return productCategories.length > 0 && !productCategories.find(category => category.id === CATEGORY_TYPE.KVG)
  }

  /**
   *  hasPersonVVG returns true if a person has at least on VVG product selected
   *  @param {object} __selectedProducts
   *  @returns {boolean}
   */
  function hasPersonVVG(__selectedProducts) {
    const productCategories = getSelectedProductsByCategory(__selectedProducts)
    return productCategories.some(category => ![CATEGORY_TYPE.KVG].includes(category.id))
  }

  /**
   *  isProductInBasket
   *  @param {string} personId
   *  @param {string} productId
   *  @returns {boolean}
   */
  function isProductInBasket(personId = isRequired('personId'), productId = isRequired('productId')) {
    const { getPerson } = usePerson()
    const person = getPerson(personId)
    return !!person.products.products[productId].selected
  }

  /**
   *  mutateProductsInBasketForPerson
   *  @param {string} personId
   *  @param {string} productId
   *  @param {{mode: string, basket: Basket}} [options={}]
   */
  function mutateProductsInBasketForPerson(personId, productId, options = {}) {
    const { getPerson, isExistingCustomer } = usePerson()
    const mode = options.mode ?? 'REPLACE'
    const ___basket = options.basket ?? null

    const pBasket = ___basket ? ___basket.persons.find(p => p.personId === personId) : getPerson(personId)
    const products = pBasket.products.products

    const addingProductIds = []
    let removingProductIds = []

    switch (mode) {
      case 'ADD':
        // we know for sure, that there's no product that needs to be replaced,
        // only add the selected product
        addingProductIds.push(productId)
        break

      case 'REMOVE':
        if (isExistingCustomer(personId)) {
          addingProductIds.push(...getReplaceableExistingProductsFromContract(getPerson(personId), productId))
        }

        removingProductIds.push(productId)
        break

      case 'REPLACE':
        if (isExistingCustomer(personId)) {
          removingProductIds.push(...getReplaceableExistingProductsFromContract(getPerson(personId), productId))

          // @note: check, if product is also selected in basket. otherwise remove from array
          removingProductIds = removingProductIds.filter(_productId => products[_productId]?.selected)
        }

        if (removingProductIds.length === 0) {
          removingProductIds.push(...getReplaceableProductsFromBasket(personId, productId))
        }

        addingProductIds.push(productId)
    }

    logInfo(['%cADDING PRODUCTS', 'color: green', addingProductIds])
    logInfo(['%cREMOVING PRODUCTS', 'color: green', removingProductIds])

    pBasket.products.products = mutatePersonProducts(products, addingProductIds, removingProductIds)
    return pBasket
  }

  /**
   *  replaceExistingProductWithSelected
   */
  function getReplaceableExistingProductsFromContract(person, productId) {
    if (!person.partnerNumber) return []

    const { getSelectedProductsOfContractPerson } = useContract()
    const { getGroupFromProduct } = useProduct()
    const groupSelected = getGroupFromProduct(productId)

    // @note: either the product is not in a group, or it's not exclusive
    if (!groupSelected || !groupSelected.exclusive) return []

    const contractProducts = getSelectedProductsOfContractPerson(person.partnerNumber)

    const contractGroupId = product =>
      Object.keys(haXProducts.groups).find(groupId => haXProducts.groups[groupId].products.includes(product.productId))

    return contractProducts
      .filter(contractProduct => contractGroupId(contractProduct) === groupSelected.id)
      .reduce(extractProductIdFromProduct, [])
  }

  async function removeProductFromPerson(personId = isRequired('personId'), productId = isRequired('productId')) {
    const pBasket = mutateProductsInBasketForPerson(personId, productId, { mode: 'REMOVE' })
    let updatedBasket = updatePersonInBasket(cloneDeep(basketStore.basket), personId, pBasket)
    updatedBasket = removeUnusedDoctors(updatedBasket)
    return await basketStore.updateOvpBasket(updatedBasket)
  }

  /**
   *  selectProductOption
   *  @param {{personId: string, product: Object, option: object, basket: Basket}}
   *  @returns {Promie<Basket>}
   */
  function selectProductOption({ personId, product, option, basket = cloneDeep(basketStore.basket) }) {
    const pBasket = basket.persons.find(p => p.personId === personId)

    // @todo: can be removed, once the old medicalOfficeSearch is removed
    const isCareProviderActive = import.meta.env.VITE_FEATURE_FLAG_CARE_PROVIDER_MODAL
    if (isCareProviderActive && option.doctor?.zsrNumber) {
      product.doctor = option.doctor
    } else if (option.zsrNumber) {
      product.doctor = option
    } else {
      product.prices = updatePriceOptions(product.prices, option)
    }

    pBasket.products.products[product.productId] = product

    return Promise.resolve(updatePersonInBasket(basket, personId, pBasket))
  }

  /**
   * selectProductOptionForCategory
   * @param {string} personId
   * @param {string} categoryId
   * @param {} option
   *
   * @returns {Promise<Basket>}
   */
  function selectProductOptionForCategory({ personId, categoryId, option }) {
    const data = {
      products: [],
      mutations: [],
    }

    const products = getProductsFromCategory(categoryId, personId)
    getGroupsFromCategory(personId, categoryId)
      .reduce((acc, val) => acc.concat(val.products), [])
      .forEach(productId => {
        const _product = products.find(p => p.productId === productId)
        if (_product) {
          data.products.push(productId)
          data.mutations.push(
            selectProductOption({
              personId,
              product: _product,
              option,
            })
          )
        }
      })

    return Promise.all(data.mutations).then(fragments => {
      const __products = fragments.reduce((acc, value, index) => {
        const productId = data.products[index]
        acc[productId] = value.persons.find(p => p.personId === personId).products.products[productId]
        return acc
      }, {})

      const __basket = fragments[0]
      const personIndex = __basket.persons.findIndex(p => p.personId === personId)

      __basket.persons[personIndex].products.products = Object.assign(
        {},
        __basket.persons[personIndex].products.products,
        __products
      )

      return Promise.resolve(__basket)
    })
  }

  async function changeProductOption({
    option,
    personId,
    product,
    basket = cloneDeep(basketStore.basket),
    params = {},
  }) {
    if (!option.zsrNumber) {
      basket = await selectProductOption({ personId, product, option, basket })
      product = basket.persons.find(p => p.personId === personId).products.products[product.productId] = Object.assign(
        {},
        product,
        basket.persons.find(p => p.personId === personId).products.products[product.productId]
      )
    } else {
      basket.persons.find(p => p.personId === personId).products.products[product.productId] = product
      product.doctor = option
    }

    basket = await basketStore.updateOvpBasket(basket, { simulate: true }, params)

    return Object.assign(
      {},
      product,
      basket.persons.find(p => p.personId === personId).products.products[product.productId]
    )
  }

  /**
   * update a product for person
   *
   * @param {String} personId
   * @param {Object} product
   * @param {Object} basket
   */
  function updateProductForPerson({ personId, product, basket = cloneDeep(basketStore.basket) }) {
    basket.persons.find(p => p.personId === personId).products.products[product.productId] = product

    return basket
  }

  /**
   *  willReplace
   *  @param {string} personId
   *  @param {string} productId
   *  @returns {}
   */
  function willReplace(personId, productId) {
    const { getReplaceableProducts } = useProduct()
    const replaceableProducts = getReplaceableProducts(productId)
    return replaceableProducts.find(__productId => getProduct(personId, __productId)?.selected)
  }

  // PRIVATE METHODS

  function extractProductIdFromProduct(acc, product) {
    acc.push(product.productId)
    return acc
  }
  /**
   * getPersonProducts
   * @param {string} personId
   * @returns {}
   */
  function getPersonProducts(personId) {
    const { getPerson } = usePerson()
    return getPerson(personId)?.products?.products ?? {}
  }

  /**
   * getProductGroupsFromCategory
   * @param {string} categoryId
   */
  function getProductGroupsFromCategory(categoryId) {
    return getSortedGroupsFromCategory(
      productStructureStore.categories.value,
      productStructureStore.groups.value,
      categoryId
    )
  }

  function getReplaceableProductsFromBasket(personId, productId) {
    const { getCategoryFromProduct, getGroupFromProduct } = useProduct()
    const category = getCategoryFromProduct(productId)
    const group = getGroupFromProduct(productId)

    if (category?.exclusive) {
      return getProductsFromCategory(category.id, personId)
        .filter(product => product.productId !== productId)
        .reduce(extractProductIdFromProduct, [])
    }

    if (group?.exclusive) {
      return getProductsFromGroup(group.id, personId)
        .filter(product => product.productId !== productId)
        .reduce(extractProductIdFromProduct, [])
    }

    return []
  }
  function mutatePersonProducts(products, addingProductIds, removingProductIds) {
    const productKeys = Object.keys(products)
    const personProducts = {}

    productKeys.forEach(productKey => {
      if (addingProductIds.includes(productKey)) {
        personProducts[productKey] = Object.assign({}, products[productKey], { selected: true })
      } else if (removingProductIds.includes(productKey)) {
        personProducts[productKey] = Object.assign({}, products[productKey], { selected: false })
      } else {
        personProducts[productKey] = products[productKey]
      }
    })

    return personProducts
  }

  return {
    // METHODS
    customizeProduct,
    getCategoriesByPersonId,
    getCategory,
    getGroupsFromCategory,
    getPriceCalculationsForProduct,
    getProduct,
    getProductMessages,
    getProductPrice,
    getProductsFromCategory,
    getProductsFromGroup,
    getReplaceableProductsFromBasket,
    getSelectedProductsByCategory,
    getSimulatedProductSelection,
    getSortedCategories,
    hasPersonKVG,
    hasPersonKVGModified,
    hasPersonOnlyKVG,
    hasPersonOnlyVVG,
    hasPersonVVG,
    hasPersonVVGModified,
    isProductInBasket,
    mutateProductsInBasketForPerson,
    removeProductFromPerson,
    selectProductOption,
    selectProductOptionForCategory,
    changeProductOption,
    willReplace,

    updateProductForPerson,
  }
}
